import { FC, useEffect } from 'react';

const ADITUDE_OUTSTREAM_INJECTED_KEY = '__RIO_ADITUDE_OUTSTREAM_INJECTED';
const ADITUDE_OUTSTREAM_ELEMENT_ID = 'outstream-player-div';
const ADITUDE_OUTSTREAM_TAG_NAME = 'aditude-outstream';
const ADITUDE_OUTSTREAM_LOG_PREFIX = '[Aditude Outstream]';
const ADITUDE_OUTSTREAM_DESKTOP_MAX_WIDTH = '300px';
const ADITUDE_OUTSTREAM_MOBILE_MAX_WIDTH = '250px';
const ADITUDE_OUTSTREAM_MOBILE_MEDIA_QUERY = '(max-width: 767px)';

const isAditudeOutstreamElement = (element: HTMLElement) =>
  element.tagName.toLowerCase() === ADITUDE_OUTSTREAM_TAG_NAME;

const getOutstreamElementById = () => document.getElementById(ADITUDE_OUTSTREAM_ELEMENT_ID);

const getAditudeOutstreamElement = () => {
  const outstreamElement = getOutstreamElementById();
  if (!outstreamElement || !isAditudeOutstreamElement(outstreamElement)) {
    return null;
  }

  return outstreamElement;
};

const getOutstreamMaxWidth = () => {
  if (window.matchMedia?.(ADITUDE_OUTSTREAM_MOBILE_MEDIA_QUERY).matches) {
    return ADITUDE_OUTSTREAM_MOBILE_MAX_WIDTH;
  }

  return ADITUDE_OUTSTREAM_DESKTOP_MAX_WIDTH;
};

const configureOutstreamElement = (outstreamElement = getAditudeOutstreamElement()) => {
  if (!outstreamElement) {
    return false;
  }

  outstreamElement.id = ADITUDE_OUTSTREAM_ELEMENT_ID;
  outstreamElement.setAttribute('slot', 'outstream');
  outstreamElement.setAttribute('dock', 'bottom-right');
  outstreamElement.setAttribute('show-close', 'true');
  outstreamElement.removeAttribute('bottom');
  outstreamElement.removeAttribute('left');
  outstreamElement.style.setProperty('--tude-video-docked-max-width', getOutstreamMaxWidth());
  outstreamElement.style.setProperty('--tude-video-docked-inset', 'auto 5px 5px auto');

  return true;
};

const createConfiguredOutstreamElement = () => {
  const outstreamElement = document.createElement(ADITUDE_OUTSTREAM_TAG_NAME);
  configureOutstreamElement(outstreamElement);
  return outstreamElement;
};

const configureOutstreamElementWithLog = () => {
  if (configureOutstreamElement()) {
    console.info(`${ADITUDE_OUTSTREAM_LOG_PREFIX} configured outstream element`, {
      dock: document.getElementById(ADITUDE_OUTSTREAM_ELEMENT_ID)?.getAttribute('dock')
    });
    return;
  }

  console.warn(`${ADITUDE_OUTSTREAM_LOG_PREFIX} outstream element was not available to configure`);
};

const appendConfiguredOutstreamElement = () => {
  const existingElement = getOutstreamElementById();
  if (existingElement) {
    if (isAditudeOutstreamElement(existingElement)) {
      configureOutstreamElement(existingElement);
      console.info(`${ADITUDE_OUTSTREAM_LOG_PREFIX} configured existing outstream element`, {
        dock: existingElement.getAttribute('dock')
      });
    } else {
      console.warn(`${ADITUDE_OUTSTREAM_LOG_PREFIX} existing outstream id is not an Aditude element`, {
        tagName: existingElement.tagName
      });
    }
    return;
  }

  const outstreamElement = createConfiguredOutstreamElement();
  document.body.appendChild(outstreamElement);
  console.info(`${ADITUDE_OUTSTREAM_LOG_PREFIX} appended configured outstream element`, {
    dock: outstreamElement.getAttribute('dock')
  });
};

export const AditudeOutstream: FC = () => {
  useEffect(() => {
    const win = window as any;
    const tude = (win.tude = win.tude || { cmd: [] });
    tude.cmd = tude.cmd || [];
    const existingElement = getOutstreamElementById();

    console.info(`${ADITUDE_OUTSTREAM_LOG_PREFIX} inject effect reached`, {
      alreadyInjected: Boolean(win[ADITUDE_OUTSTREAM_INJECTED_KEY]),
      existingElement: Boolean(existingElement),
      tudeLoaded: Boolean(tude)
    });

    if (win[ADITUDE_OUTSTREAM_INJECTED_KEY]) {
      console.info(`${ADITUDE_OUTSTREAM_LOG_PREFIX} inject skipped: already injected`);
      configureOutstreamElementWithLog();
      return;
    }

    if (existingElement) {
      console.info(`${ADITUDE_OUTSTREAM_LOG_PREFIX} inject skipped: element already exists`);
      configureOutstreamElementWithLog();
      return;
    }

    const insertOutstream = () => {
      if (getOutstreamElementById()) {
        appendConfiguredOutstreamElement();
        return;
      }

      const customElementsRegistry = win.customElements;
      if (!customElementsRegistry || typeof customElementsRegistry.whenDefined !== 'function') {
        console.warn(`${ADITUDE_OUTSTREAM_LOG_PREFIX} customElements.whenDefined is not available`);
        return;
      }

      customElementsRegistry.whenDefined(ADITUDE_OUTSTREAM_TAG_NAME).then(appendConfiguredOutstreamElement);
    };

    win[ADITUDE_OUTSTREAM_INJECTED_KEY] = true;

    console.info(`${ADITUDE_OUTSTREAM_LOG_PREFIX} queueing configured outstream element`);
    tude.cmd.push(insertOutstream);
  }, []);

  return null;
};
